/**
 * Practice HUD — overlay showing active practice mode features.
 * Displays [GOD] [NOCLIP] [INFINITE $] indicators with green/gray state.
 * Position: top-center, semi-transparent.
 * Includes a command input field with autocomplete for practice commands.
 */

import { isMobile } from '../core/device';

export interface PracticeStatus {
  godMode: boolean;
  noclip: boolean;
  infiniteMoney: boolean;
  botCount: number;
}

// ── Indicator config ──

export interface IndicatorDef {
  key: keyof Pick<PracticeStatus, 'godMode' | 'noclip' | 'infiniteMoney'>;
  label: string;
}

export const INDICATORS: IndicatorDef[] = [
  { key: 'godMode', label: 'GOD' },
  { key: 'noclip', label: 'NOCLIP' },
  { key: 'infiniteMoney', label: 'INFINITE $' },
];

export const ACTIVE_COLOR = '#44ff44';
export const INACTIVE_COLOR = '#666666';
export const HELP_TEXT = 'Type /god, /noclip, /give ak47 in chat';

/** Known commands for autocomplete. */
export const KNOWN_COMMANDS = ['god', 'noclip', 'give', 'bot_add', 'bot_kick', 'restart'] as const;

/** Known weapon names for "give" autocomplete. */
export const KNOWN_WEAPONS = [
  'ak47', 'm4a4', 'm4a1s', 'awp', 'deagle', 'usp', 'glock', 'p250',
  'fiveseven', 'tec9', 'famas', 'galil', 'aug', 'sg553', 'mp9', 'mac10',
  'p90', 'nova', 'ssg08',
] as const;

export type CommandCallback = (cmd: string, args: string[]) => void;

// ── Pure logic helpers (testable without DOM) ──

/**
 * Returns the color for an indicator based on its active state.
 */
export function getIndicatorColor(active: boolean): string {
  return active ? ACTIVE_COLOR : INACTIVE_COLOR;
}

/**
 * Returns the text shadow for an indicator based on its active state.
 */
export function getIndicatorShadow(active: boolean): string {
  return active ? `0 0 8px ${ACTIVE_COLOR}` : 'none';
}

/**
 * Build the list of indicator states from a PracticeStatus.
 * Returns an array of { label, active, color, shadow } for each indicator.
 */
export function buildIndicatorStates(status: PracticeStatus): Array<{
  key: string;
  label: string;
  active: boolean;
  color: string;
  shadow: string;
}> {
  return INDICATORS.map((ind) => {
    const active = status[ind.key];
    return {
      key: ind.key,
      label: ind.label,
      active,
      color: getIndicatorColor(active),
      shadow: getIndicatorShadow(active),
    };
  });
}

/**
 * Parse a command string (with or without leading "/") into command name + args.
 * Returns { cmd, args } or null if the input is empty/whitespace.
 */
export function parseCommand(input: string): { cmd: string; args: string[] } | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const withoutSlash = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
  const parts = withoutSlash.split(/\s+/);
  const cmd = parts[0].toLowerCase();
  const args = parts.slice(1);
  return cmd ? { cmd, args } : null;
}

/**
 * Get autocomplete suggestions for the current input text.
 * Returns an array of suggestion strings.
 */
export function getAutocompleteSuggestions(input: string): string[] {
  const trimmed = input.trim();
  const withoutSlash = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;

  if (!withoutSlash) {
    return [...KNOWN_COMMANDS];
  }

  const parts = withoutSlash.split(/\s+/);

  // If typing the first word (command name), filter commands
  if (parts.length === 1) {
    const partial = parts[0].toLowerCase();
    return KNOWN_COMMANDS.filter((c) => c.startsWith(partial));
  }

  // If command is "give" and typing second word, show weapon suggestions
  if (parts[0].toLowerCase() === 'give' && parts.length === 2) {
    const partial = parts[1].toLowerCase();
    return KNOWN_WEAPONS.filter((w) => w.startsWith(partial));
  }

  return [];
}

// ── DOM state ──

let container: HTMLElement | null = null;
let indicatorEls: Map<string, HTMLElement> = new Map();
let helpEl: HTMLElement | null = null;
let helpCollapsed = false;
let commandInput: HTMLInputElement | null = null;
let autocompleteDropdown: HTMLElement | null = null;
let commandCallback: CommandCallback | null = null;
let selectedAutocompleteIndex = -1;
let hudLifecycleToken = 0;

/**
 * Register a callback for when the user submits a command from the practice HUD.
 */
export function onCommand(callback: CommandCallback): void {
  commandCallback = callback;
}

function updateAutocomplete(inputEl: HTMLInputElement, dropdownEl: HTMLElement): void {
  const value = inputEl.value;
  const suggestions = getAutocompleteSuggestions(value);
  selectedAutocompleteIndex = -1;

  // Clear existing
  while (dropdownEl.firstChild) {
    dropdownEl.removeChild(dropdownEl.firstChild);
  }

  if (suggestions.length === 0 || (suggestions.length === 1 && suggestions[0] === value.replace(/^\//, '').trim())) {
    dropdownEl.style.display = 'none';
    return;
  }

  dropdownEl.style.display = 'block';

  for (let i = 0; i < suggestions.length; i++) {
    const item = document.createElement('div');
    item.setAttribute('data-autocomplete-item', String(i));
    Object.assign(item.style, {
      padding: '4px 8px',
      cursor: 'pointer',
      fontSize: '12px',
      color: '#cccccc',
      fontFamily: '"Courier New", monospace',
    });
    item.textContent = suggestions[i];
    item.addEventListener('mouseenter', () => {
      item.style.background = 'rgba(68,255,68,0.15)';
    });
    item.addEventListener('mouseleave', () => {
      item.style.background = 'transparent';
    });
    item.addEventListener('mousedown', (e) => {
      e.preventDefault();
      applyAutocomplete(suggestions[i], inputEl, dropdownEl);
    });
    dropdownEl.appendChild(item);
  }
}

function applyAutocomplete(suggestion: string, inputEl: HTMLInputElement, dropdownEl: HTMLElement): void {
  const value = inputEl.value;
  const withoutSlash = value.startsWith('/') ? value.slice(1) : value;
  const parts = withoutSlash.split(/\s+/);

  if (parts.length <= 1) {
    // Replacing the command
    inputEl.value = suggestion + ' ';
  } else {
    // Replacing the argument (e.g. weapon name after "give ")
    inputEl.value = parts[0] + ' ' + suggestion + ' ';
  }
  inputEl.focus();
  updateAutocomplete(inputEl, dropdownEl);
}

function submitCommand(inputEl: HTMLInputElement, dropdownEl: HTMLElement): void {
  const parsed = parseCommand(inputEl.value);
  if (parsed && commandCallback) {
    commandCallback(parsed.cmd, parsed.args);
  }
  inputEl.value = '';
  dropdownEl.style.display = 'none';
}

function ensureHud(): void {
  if (container) return;

  // F-UI-010: Bump the lifecycle token at the very top so every listener
  // attached during this ensureHud() pass closes over the same token.
  // disposePracticeHud() also bumps it; stale closures (auto-complete blur,
  // helpWrapper click) early-return when their captured token no longer
  // matches the live module token.
  const lifecycleToken = ++hudLifecycleToken;

  const mobile = isMobile();

  container = document.createElement('div');
  container.id = 'practice-hud';
  Object.assign(container.style, {
    position: 'fixed',
    // BUG-P-31: On mobile, top: 8px collides with the round-score widget.
    // Push the HUD down to clear it.
    top: mobile ? '80px' : '8px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    zIndex: '50',
    pointerEvents: 'none',
    fontFamily: '"Courier New", monospace',
    opacity: '0.85',
  });

  // Indicator row
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  });

  for (const ind of INDICATORS) {
    const el = document.createElement('span');
    el.setAttribute('data-practice-indicator', ind.key);
    Object.assign(el.style, {
      fontSize: '13px',
      fontWeight: 'bold',
      letterSpacing: '1px',
      padding: '2px 8px',
      borderRadius: '3px',
      border: '1px solid',
      textTransform: 'uppercase',
      color: INACTIVE_COLOR,
      borderColor: INACTIVE_COLOR,
      background: 'rgba(0,0,0,0.5)',
    });
    el.textContent = ind.label;
    row.appendChild(el);
    indicatorEls.set(ind.key, el);
  }

  container.appendChild(row);

  // Help text (collapsible)
  const helpWrapper = document.createElement('div');
  Object.assign(helpWrapper.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '2px',
    pointerEvents: 'auto',
    cursor: 'pointer',
  });

  const helpToggle = document.createElement('span');
  Object.assign(helpToggle.style, {
    fontSize: '10px',
    color: '#666666',
  });
  helpToggle.textContent = helpCollapsed ? '[+]' : '[-]';
  helpWrapper.appendChild(helpToggle);

  helpEl = document.createElement('div');
  Object.assign(helpEl.style, {
    fontSize: '10px',
    color: '#999999',
    letterSpacing: '0.5px',
    textAlign: 'center',
  });
  helpEl.textContent = HELP_TEXT;
  if (helpCollapsed) helpEl.style.display = 'none';
  helpWrapper.appendChild(helpEl);

  helpWrapper.addEventListener('click', () => {
    // F-UI-010: Don't mutate module-scope state if the HUD has been disposed
    // and rebuilt under us — the stale closure still holds the prior helpEl.
    if (lifecycleToken !== hudLifecycleToken) return;
    helpCollapsed = !helpCollapsed;
    if (helpEl) helpEl.style.display = helpCollapsed ? 'none' : '';
    helpToggle.textContent = helpCollapsed ? '[+]' : '[-]';
  });

  container.appendChild(helpWrapper);

  // Command input area
  const inputWrapper = document.createElement('div');
  inputWrapper.setAttribute('data-practice-input-wrapper', '1');
  Object.assign(inputWrapper.style, {
    // BUG-P-10: On mobile the OS keyboard covers the bottom 40% of the
    // screen, so even a top-anchored command input gets occluded the moment
    // it's focused. Hide the entire input wrapper on touch devices — mobile
    // players use the chat composer for practice commands instead.
    display: mobile ? 'none' : 'flex',
    position: 'relative',
    alignItems: 'center',
    marginTop: '6px',
    pointerEvents: 'auto',
  });

  const prefix = document.createElement('span');
  Object.assign(prefix.style, {
    fontSize: '13px',
    color: '#44ff44',
    fontWeight: 'bold',
    fontFamily: '"Courier New", monospace',
    marginRight: '2px',
  });
  prefix.textContent = '/';
  inputWrapper.appendChild(prefix);

  commandInput = document.createElement('input');
  commandInput.type = 'text';
  commandInput.placeholder = 'Type command...';
  commandInput.setAttribute('data-practice-command-input', '1');
  Object.assign(commandInput.style, {
    background: 'rgba(0,0,0,0.6)',
    border: '1px solid #444444',
    borderRadius: '3px',
    color: '#ffffff',
    fontFamily: '"Courier New", monospace',
    fontSize: '12px',
    padding: '4px 8px',
    width: '200px',
    outline: 'none',
  });
  const currentInput = commandInput;
  // F-UI-010: lifecycleToken is captured at the top of ensureHud — the
  // autocomplete blur uses the same token as helpWrapper, so a single
  // disposePracticeHud() bump invalidates every stale closure together.

  commandInput.addEventListener('input', () => {
    if (!autocompleteDropdown) return;
    updateAutocomplete(currentInput, autocompleteDropdown);
  });

  commandInput.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // If autocomplete is open and an item is selected, apply it
      if (autocompleteDropdown && autocompleteDropdown.style.display === 'block' && selectedAutocompleteIndex >= 0) {
        const items = autocompleteDropdown.querySelectorAll('[data-autocomplete-item]');
        if (items[selectedAutocompleteIndex]) {
          applyAutocomplete(items[selectedAutocompleteIndex].textContent ?? '', currentInput, autocompleteDropdown);
          return;
        }
      }
      if (autocompleteDropdown) submitCommand(currentInput, autocompleteDropdown);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (autocompleteDropdown && autocompleteDropdown.style.display === 'block') {
        const items = autocompleteDropdown.querySelectorAll('[data-autocomplete-item]');
        if (items.length === 0) return;
        // Remove highlight from current
        if (selectedAutocompleteIndex >= 0 && items[selectedAutocompleteIndex]) {
          (items[selectedAutocompleteIndex] as HTMLElement).style.background = 'transparent';
        }
        if (e.key === 'ArrowDown') {
          selectedAutocompleteIndex = (selectedAutocompleteIndex + 1) % items.length;
        } else {
          selectedAutocompleteIndex = selectedAutocompleteIndex <= 0 ? items.length - 1 : selectedAutocompleteIndex - 1;
        }
        (items[selectedAutocompleteIndex] as HTMLElement).style.background = 'rgba(68,255,68,0.15)';
      }
    } else if (e.key === 'Escape') {
      if (autocompleteDropdown) autocompleteDropdown.style.display = 'none';
    }
  });

  commandInput.addEventListener('blur', () => {
    // Delay hiding so mousedown on autocomplete item fires first
    setTimeout(() => {
      if (lifecycleToken !== hudLifecycleToken) return;
      if (autocompleteDropdown) autocompleteDropdown.style.display = 'none';
    }, 150);
  });

  inputWrapper.appendChild(commandInput);

  // Autocomplete dropdown
  autocompleteDropdown = document.createElement('div');
  autocompleteDropdown.setAttribute('data-practice-autocomplete', '1');
  Object.assign(autocompleteDropdown.style, {
    position: 'absolute',
    top: '100%',
    left: '14px',
    width: '200px',
    background: 'rgba(0,0,0,0.85)',
    border: '1px solid #444444',
    borderRadius: '3px',
    display: 'none',
    maxHeight: '150px',
    overflowY: 'auto',
    zIndex: '60',
  });
  inputWrapper.appendChild(autocompleteDropdown);

  container.appendChild(inputWrapper);

  if (mobile) {
    // BUG-P-10: Replacement note for the hidden command input. Tells mobile
    // players to use the chat composer instead.
    const mobileNote = document.createElement('div');
    mobileNote.setAttribute('data-practice-mobile-note', '1');
    Object.assign(mobileNote.style, {
      fontSize: '10px',
      color: '#999999',
      letterSpacing: '0.5px',
      textAlign: 'center',
      marginTop: '6px',
      fontStyle: 'italic',
    });
    mobileNote.textContent = 'Commands are desktop-only';
    container.appendChild(mobileNote);
  }

  document.body.appendChild(container);
}

/**
 * Update the practice HUD with the current status.
 * Creates the HUD if it doesn't exist.
 */
export function updatePracticeHud(status: PracticeStatus): void {
  ensureHud();

  container!.style.display = 'flex';

  const states = buildIndicatorStates(status);
  for (const state of states) {
    const el = indicatorEls.get(state.key);
    if (!el) continue;
    el.style.color = state.color;
    el.style.borderColor = state.color;
    el.style.textShadow = state.shadow;
  }
}

/**
 * Hide the practice HUD without removing it from the DOM.
 */
export function hidePracticeHud(): void {
  if (container) {
    container.style.display = 'none';
  }
}

/**
 * Dispose the practice HUD, removing it from the DOM entirely.
 */
export function disposePracticeHud(): void {
  hudLifecycleToken++;
  if (container) {
    container.remove();
    container = null;
  }
  indicatorEls.clear();
  helpEl = null;
  commandInput = null;
  autocompleteDropdown = null;
  commandCallback = null;
  helpCollapsed = false;
  selectedAutocompleteIndex = -1;
}
